import { create } from 'zustand';
import Cookies from 'js-cookie';
import { authApi, LoginResponse, SuperAdmin } from '../api/auth';

interface AuthState {
  isAuthenticated: boolean;
  superAdmin: SuperAdmin | null;
  tempToken: string | null;
  setupToken: string | null;
  requires2FA: boolean;
  requires2FASetup: boolean;
  backupCodes: string[] | null;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<LoginResponse>;
  verify2FA: (code: string) => Promise<void>;
  setup2FA: () => Promise<{ secret: string; qrCodeUrl: string }>;
  enable2FA: (secret: string, code: string) => Promise<string[]>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;
  clearBackupCodes: () => void;
  setSetupToken: (token: string) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: !!Cookies.get('admin_token'),
  superAdmin: null,
  tempToken: sessionStorage.getItem('sa_temp_token') || null,
  setupToken: sessionStorage.getItem('sa_setup_token') || null,
  requires2FA: false,
  requires2FASetup: false,
  backupCodes: null,
  isLoading: false,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authApi.login(email, password);

      if (response.requires2FASetup) {
        sessionStorage.setItem('sa_setup_token', response.setupToken!);
        set({
          requires2FASetup: true,
          setupToken: response.setupToken,
          isLoading: false,
        });
      } else if (response.requires2FA) {
        sessionStorage.setItem('sa_temp_token', response.tempToken!);
        set({
          requires2FA: true,
          tempToken: response.tempToken,
          isLoading: false,
        });
      } else {
        // Direct authentication — no 2FA required
        set({ isLoading: false });
      }

      return response;
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Login failed';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  verify2FA: async (code: string) => {
    const { tempToken } = get();
    if (!tempToken) throw new Error('No temp token');

    set({ isLoading: true, error: null });
    try {
      const { accessToken } = await authApi.verify2FA(tempToken, code);
      Cookies.set('admin_token', accessToken, {
        expires: 1,
        secure: import.meta.env.PROD,
        sameSite: 'strict',
      });
      sessionStorage.removeItem('sa_temp_token');
      set({
        isAuthenticated: true,
        tempToken: null,
        requires2FA: false,
        isLoading: false,
      });
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Invalid code';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setup2FA: async () => {
    const { setupToken } = get();
    if (!setupToken) throw new Error('No setup token');

    set({ isLoading: true, error: null });
    try {
      const result = await authApi.setup2FA(setupToken);
      set({ isLoading: false });
      return result;
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to setup 2FA';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  enable2FA: async (secret: string, code: string) => {
    const { setupToken } = get();
    if (!setupToken) throw new Error('No setup token');

    set({ isLoading: true, error: null });
    try {
      const { accessToken, backupCodes } = await authApi.enable2FA(
        setupToken,
        secret,
        code,
      );
      Cookies.set('admin_token', accessToken, {
        expires: 1,
        secure: import.meta.env.PROD,
        sameSite: 'strict',
      });
      sessionStorage.removeItem('sa_setup_token');
      set({
        isAuthenticated: true,
        setupToken: null,
        requires2FASetup: false,
        backupCodes,
        isLoading: false,
      });
      return backupCodes;
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Invalid code';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      Cookies.remove('admin_token');
      sessionStorage.removeItem('sa_temp_token');
      sessionStorage.removeItem('sa_setup_token');
      set({
        isAuthenticated: false,
        superAdmin: null,
        tempToken: null,
        setupToken: null,
        requires2FA: false,
        requires2FASetup: false,
        backupCodes: null,
      });
    }
  },

  loadUser: async () => {
    set({ isLoading: true });
    try {
      const superAdmin = await authApi.getMe();
      set({ superAdmin, isLoading: false });
    } catch (error) {
      Cookies.remove('admin_token');
      set({ isAuthenticated: false, superAdmin: null, isLoading: false });
    }
  },

  clearError: () => set({ error: null }),

  clearBackupCodes: () => set({ backupCodes: null }),

  setSetupToken: (token: string) => {
    sessionStorage.setItem('sa_setup_token', token);
    set({ setupToken: token, requires2FASetup: true });
  },
}));
